import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { createPolicy, updatePolicy } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

const SCOPE_OPTIONS = ["All", "Slack", "Teams", "Zendesk", "Intercom", "Confluence", "Guru", "Notion"];
const DOMAIN_OPTIONS = ["All", "Support", "EU Payments", "Global Refunds", "Consumer Support", "Support Enablement"];

export default function PolicyForm({ open, onOpenChange, policy, onSaved }) {
  const isEdit = Boolean(policy?.id);
  const [name, setName] = useState("");
  const [summary, setSummary] = useState("");
  const [scope, setScope] = useState([]);
  const [domains, setDomains] = useState([]);
  const [version, setVersion] = useState("v1");
  const [approvedBy, setApprovedBy] = useState("Legal");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(policy?.name || "");
      setSummary(policy?.summary || "");
      setScope(policy?.scope || []);
      setDomains(policy?.domains || []);
      setVersion(policy?.version || "v1");
      setApprovedBy(policy?.approved_by || "Legal");
    }
  }, [open, policy]);

  const toggle = (setter, list, value) => {
    setter(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  };

  const save = async () => {
    if (!name.trim() || !summary.trim()) {
      toast.error("Name and summary are required");
      return;
    }
    setSaving(true);
    try {
      const body = { name, summary, scope, domains, version, approved_by: approvedBy };
      const saved = isEdit ? await updatePolicy(policy.id, body) : await createPolicy(body);
      toast.success(isEdit ? "Policy updated" : `Policy created · ${saved.id}`);
      onSaved?.(saved);
      onOpenChange(false);
    } catch {
      toast.error("Could not save policy");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="policy-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">
            {isEdit ? `Edit policy · ${policy.id}` : "New policy"}
          </DialogTitle>
          <DialogDescription>
            Policies gate extraction, review, and publication. Version bumps create an audit event.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="policy-form-name"
              placeholder="e.g. PII-sensitive source mix requires compliance review"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand"
            />
          </Field>
          <Field label="Summary">
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              data-testid="policy-form-summary"
              rows={3}
              placeholder="Plain-English statement of the rule, scope, and intent."
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Version">
              <input
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                data-testid="policy-form-version"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand"
              />
            </Field>
            <Field label="Approved by">
              <input
                value={approvedBy}
                onChange={(e) => setApprovedBy(e.target.value)}
                data-testid="policy-form-approver"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand"
              />
            </Field>
          </div>

          <Field label="Scope">
            <div className="flex flex-wrap gap-1.5">
              {SCOPE_OPTIONS.map((s) => (
                <Chip key={s} active={scope.includes(s)} onClick={() => toggle(setScope, scope, s)}>{s}</Chip>
              ))}
            </div>
          </Field>

          <Field label="Domains">
            <div className="flex flex-wrap gap-1.5">
              {DOMAIN_OPTIONS.map((d) => (
                <Chip key={d} active={domains.includes(d)} onClick={() => toggle(setDomains, domains, d)}>{d}</Chip>
              ))}
            </div>
          </Field>
        </div>

        <DialogFooter className="mt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-border bg-card px-4 py-2 text-sm hover:bg-muted"
            data-testid="policy-form-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded-md bg-brand text-brand-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60"
            data-testid="policy-form-save"
          >
            {saveButtonLabel(saving, isEdit)}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const saveButtonLabel = (saving, isEdit) => {
  if (saving) return "Saving…";
  if (isEdit) return "Save changes";
  return "Create policy";
};

function Field({ label, children }) {
  return (
    <div>
      <div className="tracked-caps mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function Chip({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${
        active
          ? "bg-brand text-brand-foreground border-brand"
          : "bg-card text-foreground border-border hover:bg-muted"
      }`}
      data-testid={`policy-form-chip-${String(children).toLowerCase().replace(/\s+/g, "-")}`}
    >
      {children}
      {active && <X className="h-3 w-3" />}
    </button>
  );
}
